import React, { useState, useEffect, useCallback } from 'react';
import { searchExercises, addExercise, deleteExercise, updateExercise } from '../api';
import DataTable from 'react-data-table-component';
import { Modal, Button } from 'react-bootstrap';

const ExerciseLibrary = () => {
    const [exercises, setExercises] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [newExercise, setNewExercise] = useState({
        name: '',
        category: '',
        muscle_groups: '',
        equipment_required: '',
        difficulty_level: 'Beginner',
        instructions: '',
        video_url: ''
    });

    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedExercise, setSelectedExercise] = useState(null);

    const fetchExercises = useCallback(async () => {
        const response = await searchExercises(searchTerm);
        setExercises(response.data);
    }, [searchTerm]);

    useEffect(() => {
        fetchExercises();
    }, [fetchExercises]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewExercise({ ...newExercise, [name]: value });
    };

    const handleAddExercise = async (e) => {
        e.preventDefault();
        await addExercise(newExercise);
        setNewExercise({
            name: '',
            category: '',
            muscle_groups: '',
            equipment_required: '',
            difficulty_level: 'Beginner',
            instructions: '',
            video_url: ''
        });
        setShowAddForm(false);
        fetchExercises();
    };

    const handleDeleteExercise = async (exerciseId) => {
        if (window.confirm('Are you sure you want to delete this exercise?')) {
            await deleteExercise(exerciseId);
            fetchExercises();
        }
    };

    const handleShowEditModal = (exercise) => {
        setSelectedExercise(exercise);
        setShowEditModal(true);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setSelectedExercise(null);
    };

    const handleUpdateExercise = async (e) => {
        e.preventDefault();
        await updateExercise(selectedExercise);
        handleCloseEditModal();
        fetchExercises();
    };

    const columns = [
        {
            name: 'Name',
            selector: row => row.name,
            sortable: true,
        },
        {
            name: 'Category',
            selector: row => row.category,
            sortable: true,
        },
        {
            name: 'Muscle Groups',
            selector: row => row.muscle_groups,
            sortable: true,
        },
        {
            name: 'Equipment',
            selector: row => row.equipment_required,
            sortable: true,
        },
        {
            name: 'Difficulty',
            selector: row => row.difficulty_level,
            sortable: true,
        },
        {
            name: 'Actions',
            cell: row => (
                <div>
                    <button className="btn btn-primary btn-sm me-2" onClick={() => handleShowEditModal(row)}>Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDeleteExercise(row.exercise_id)}>Delete</button>
                </div>
            )
        }
    ];

    return (
        <div className="container-fluid p-4">
            <h1 className="text-2xl font-bold mb-4">Exercise Library</h1>
            <div className="flex justify-content-between mb-4">
                <input
                    type="text"
                    placeholder="Search exercises..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-control w-75"
                />
                <button onClick={() => setShowAddForm(!showAddForm)} className={`btn ${showAddForm ? 'btn-secondary' : 'btn-primary'} ml-2`}>
                    {showAddForm ? 'Cancel' : 'Add Exercise'}
                </button>
            </div>
            {showAddForm && (
                <form onSubmit={handleAddExercise} className="p-4 border rounded mb-4">
                    <div className="row g-3">
                        <div className="col-md-6"><input type="text" name="name" placeholder="Name" value={newExercise.name} onChange={handleInputChange} className="form-control" /></div>
                        <div className="col-md-6"><input type="text" name="category" placeholder="Category" value={newExercise.category} onChange={handleInputChange} className="form-control" /></div>
                        <div className="col-md-6"><input type="text" name="muscle_groups" placeholder="Muscle Groups" value={newExercise.muscle_groups} onChange={handleInputChange} className="form-control" /></div>
                        <div className="col-md-6"><input type="text" name="equipment_required" placeholder="Equipment Required" value={newExercise.equipment_required} onChange={handleInputChange} className="form-control" /></div>
                        <div className="col-md-6">
                            <select name="difficulty_level" value={newExercise.difficulty_level} onChange={handleInputChange} className="form-select">
                                <option>Beginner</option>
                                <option>Intermediate</option>
                                <option>Advanced</option>
                            </select>
                        </div>
                        <div className="col-md-6"><input type="text" name="video_url" placeholder="Video URL" value={newExercise.video_url} onChange={handleInputChange} className="form-control" /></div>
                        <div className="col-12"><textarea name="instructions" placeholder="Instructions" value={newExercise.instructions} onChange={handleInputChange} className="form-control"></textarea></div>
                    </div>
                    <button type="submit" className="btn btn-primary mt-3">Add Exercise</button>
                </form>
            )}
            <DataTable
                columns={columns}
                data={exercises}
                pagination
                highlightOnHover
                striped
                className="table table-sm"
            />

            {selectedExercise && (
                <Modal show={showEditModal} onHide={handleCloseEditModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>Edit Exercise</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <form onSubmit={handleUpdateExercise}>
                            <div className="mb-3">
                                <label className="form-label">Name</label>
                                <input type="text" className="form-control" value={selectedExercise.name} onChange={(e) => setSelectedExercise({ ...selectedExercise, name: e.target.value })} />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Category</label>
                                <input type="text" className="form-control" value={selectedExercise.category} onChange={(e) => setSelectedExercise({ ...selectedExercise, category: e.target.value })} />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Muscle Groups</label>
                                <input type="text" className="form-control" value={selectedExercise.muscle_groups} onChange={(e) => setSelectedExercise({ ...selectedExercise, muscle_groups: e.target.value })} />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Equipment</label>
                                <input type="text" className="form-control" value={selectedExercise.equipment_required} onChange={(e) => setSelectedExercise({ ...selectedExercise, equipment_required: e.target.value })} />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Difficulty</label>
                                <select className="form-select" value={selectedExercise.difficulty_level} onChange={(e) => setSelectedExercise({ ...selectedExercise, difficulty_level: e.target.value })}>
                                    <option>Beginner</option>
                                    <option>Intermediate</option>
                                    <option>Advanced</option>
                                </select>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Instructions</label>
                                <textarea className="form-control" value={selectedExercise.instructions} onChange={(e) => setSelectedExercise({ ...selectedExercise, instructions: e.target.value })}></textarea>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Video URL</label>
                                <input type="text" className="form-control" value={selectedExercise.video_url} onChange={(e) => setSelectedExercise({ ...selectedExercise, video_url: e.target.value })} />
                            </div>
                            <Button variant="primary" type="submit">
                                Save Changes
                            </Button>
                        </form>
                    </Modal.Body>
                </Modal>
            )}
        </div>
    );
};

export default ExerciseLibrary;